import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import userInfoByAttribute from '@nykaa/auth-shared/store/actions/userInfo';
import { setPaidEarlyAccessFromCookie } from '@nykaa/auth-shared/store/actions/paidEarlyAccess';
import { usePaidEarlyAccess } from '@nykaa/auth-shared/hooks';
import { fetchSegmentOverNetwork } from '@nykaa/auth-shared/store/helpers';
import { useUserAttributes } from '@nykaa/auth-shared/store/helpers/userAttributes';
import { ATTRIBUTE_AFFILIATE } from '@nykaa/auth-shared/store/constants';
import { addAttribute } from '@nykaa/auth-shared/utils';
import {
  IS_REPEAT_USER,
  REPEAT_USER_EXPIRY_DAYS,
  ATTRIBUTE_USER_SEGMENT,
  REPEAT_USER_ONE,
  REPEAT_USER_ZERO,
  USER_SEGMENT_COOKIE_ERROR,
  ATTRIBUTE_PAID_EARLY_ACCESS,
  PAID_EARLY_ACCESS_KEY,
  PAID_EARLY_ACCESS_DEFAULT_VALUE,
} from '@nykaa/auth-shared/store/constants';
import appEvents from 'store/app/actionTypes';
import PageTypes from 'constants/pageTypes';
import {
  trackUser,
  getCartCountFromStorage,
  setLoginLocation,
} from 'store/auth/helper';
import setUpSessionCount, {
  setSiteVisitSessionCount,
  setUpPdpSessionCount,
} from 'helpers/utils/sessionCount';
import useLLMSourceTracking from 'hooks/useLLMSourceTracking';
import { addAutoSignInEvent } from 'shared/store/smartLock/autoSignin';
import { getEligibleForVariant } from 'shared/helpers/experimentV2';
import { storeCredentials } from 'store/smartLock/helper';
import { RequestConfig } from 'api';
import { handleStoreId } from 'helpers/store';
import { handleDevice } from 'helpers/deviceIdentity';
import { setCookie } from '@nykaa/utils/cookies';
import { logger } from '@nykaa/logger';
import { pushData } from '@nykaa/data-layer/utils';
import {
  getAuthSteps,
  deleteAuthSteps,
  isAutoFillShown,
  setAutoFillShown,
  getMobileNumberLocal,
  getRegistration,
  deleteRegistration,
} from 'shared/helpers/auth';
import { getPageType } from 'shared/components/Header/helper';
import { loadVerloop } from 'shared/store/auth/helper';
import {
  AUTH_URL,
  GOOGLE_ONE_EXPERIEMNT,
  LOGIN_LOCATION,
  MOBILE_VERIFICATION_URL,
  PRIVE_TIER_KEY,
} from 'shared/constants/auth';
import { pushMobileVerificationEvents } from 'shared/User/helpers/mobileVerification';
import trackingEvent, {
  LOGIN_CODES,
} from 'shared/User/constants/mobileVerification/trackingEvents';
import checkValidHost from '@nykaa/server/utils';
import WishList from './Wishlist';
import { State } from 'store/types';
import { deleteCookie, getCookie } from 'helpers/utils/cookie';
import {
  ATTRIBUTE_INSTALL_OFFER,
  EXP_AB_AUTOFILL,
  IOC_EXPIRATION_TIME,
  IOC_VALUE,
} from 'shared/constants';
import { styled } from '@nykaa/ui-components';
import { useIsAffiliateLinkSharingEnabled } from 'hooks/affiliateSharing';
import {
  AB_IOC_CART_NUDGE,
  CART_STEP_COUNTER,
  DW_APP_BANNER,
} from 'constants/experiments';
import { localStorageService } from 'service/storage';
import { setIocValue } from 'store/auth/action';
import { useIsTagsV2Enabled } from 'hooks/product';
import { useIsLoggedIn } from 'hooks/auth';
import { useIsConvenienceFeeEnabled } from 'hooks';
import { useGetBaseDataCartApi } from 'hooks/cart/useGetCartData';
import { cartLiteApi } from 'store/cartLiteApi/action';
import { isPathEnabled } from 'shared/helpers/utils/url';

interface BootProps {
  configFlagsFetched: boolean;
  fetchAuth: () => Promise<void>;
  fetchPriveTier: () => Promise<void>;
  fetchUserHash: () => Promise<void>;
  email: string;
  firstName: string;
  formKey: string;
  id: string;
  isPro: boolean;
  groupId: string;
  dataFetched: boolean;
  fetchedOverNetwork: boolean;
  userName: string;
  storeId: string;
  isStoreEnabled: boolean;
  trackDeviceId: boolean;
  suggestMobile: () => Promise<any>;
  isSuggestedMobileMap: boolean;
  vrnRegionEligibility?: string;
  vrnExpState?: string;
  domains: [];
  isAutoSignUp: boolean;
  showSignUp: () => Promise<void>;
  showChatNow?: boolean;
  isPDPBannerShown: boolean;
  productId: string;
  bucketId: string;
  isPricesRevealedActive: boolean;
  userSegmentConfig: { [key: string]: string };
  isUserSegmentEnabled: boolean;
  showProfile: () => Promise<void>;
  isDifferentialPricingEnabled: boolean;
  priveBand: string;
}

const Iframe = styled.iframe`
  height: 100vh;
  width: 100%;
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius40};
  height: 50%;
  position: fixed;
  bottom: 0;
  border-bottom-left-radius: ${({ theme }) => theme.borders.radiusNone};
  border-bottom-right-radius: ${({ theme }) => theme.borders.radiusNone};
`;

const FrameWrapper = styled.div`
  position: fixed;
  width: 100%;
  background: ${({ theme }) => theme.colors.textDisabled};
  z-index: 4;
  height: 100%;
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius40};
`;

const Boot = ({
  configFlagsFetched,
  fetchAuth,
  email,
  firstName,
  formKey,
  id,
  isPro,
  groupId,
  dataFetched,
  userName,
  fetchedOverNetwork,
  storeId,
  isStoreEnabled,
  trackDeviceId,
  suggestMobile,
  isSuggestedMobileMap,
  vrnRegionEligibility,
  vrnExpState,
  domains,
  isAutoSignUp,
  showSignUp,
  fetchUserHash,
  isPDPBannerShown,
  productId,
  showChatNow,
  bucketId,
  isPricesRevealedActive,
  userSegmentConfig,
  isUserSegmentEnabled,
  isDifferentialPricingEnabled,
  showProfile,
  fetchPriveTier,
  priveBand,
}: BootProps) => {
  const { pathname } = useLocation();
  const isExperimentEnabled = useSelector(
    (state: State) => state?.langConfig?.isExperimentEnabled
  );
  const isZendeskEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.zendeskChatEnabled || false
  );

  const { isSmartLockActive } =
    useSelector((state: State) => state.remoteConfig.configs.configFlags) || {};
  const pageType = getPageType(pathname);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const { repeatUser = '' } = userSegmentConfig || {};
  const dispatch = useDispatch();
  const isTagsV2Enabled = useIsTagsV2Enabled();
  const isAffiliateExpEnabled = useIsAffiliateLinkSharingEnabled();
  const { fetchUserAttributes, loadCachedAffiliateData, storeAffiliateData } =
    useUserAttributes();
  const isLoggedIn = useIsLoggedIn();
  const isUserLoggedIn = useIsLoggedIn();
  const { enabled: paidEarlyAccessEnabled = false } = useSelector(
    (state: State) => state.remoteConfig.configs?.paidEarlyAccessSegment || {}
  );
  const { processPaidEarlyAccess } = usePaidEarlyAccess();
  const isConvenienceFeeEnabled = useIsConvenienceFeeEnabled();
  const { apiParams } = useGetBaseDataCartApi();

  const stepCounterExpEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, CART_STEP_COUNTER, 'A')
  );

  const { stepperEnabled } =
    useSelector(
      (state: State) => state.remoteConfig.configs?.productRackConfig
    ) || {};

  const { status: cartApiConfigEnabled, enabledPaths } =
    useSelector((state: State) => state.remoteConfig.configs?.cartApiConfig) ||
    {};
  useEffect(() => {
    if (isStoreEnabled) {
      handleStoreId(storeId);
    }
    if (isExperimentEnabled) {
      const trackingData = { vrnRegionEligibility, vrnExpState };
      if (!vrnExpState) {
        trackingData.vrnRegionEligibility = 'Error';
      }
      pushData({ ...trackingData });
    }
  }, [
    storeId,
    isStoreEnabled,
    isExperimentEnabled,
    vrnRegionEligibility,
    vrnExpState,
  ]);

  // Track LLM platform acquisition using remote config
  useLLMSourceTracking();

  useEffect(() => {
    setUpSessionCount();
    pushData({ isTagsV2Enabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSiteVisitSessionCount();
  }, []);

  const getAttributes = useCallback(() => {
    let attri = '';
    if (isUserSegmentEnabled) attri += ATTRIBUTE_USER_SEGMENT;
    if (isAffiliateExpEnabled) attri = addAttribute(attri, ATTRIBUTE_AFFILIATE);
    if (paidEarlyAccessEnabled && isUserLoggedIn) {
      attri = addAttribute(attri, ATTRIBUTE_PAID_EARLY_ACCESS);
    }
    return attri;
  }, [
    isUserSegmentEnabled,
    isAffiliateExpEnabled,
    paidEarlyAccessEnabled,
    isUserLoggedIn,
  ]);

  const handleFetchUserAttributes = useCallback(() => {
    const attributes = getAttributes();
    if (!attributes) return;
    const isRepeatUser = getCookie(IS_REPEAT_USER);
    const isNewUser = !(isRepeatUser && Number(isRepeatUser));

    const shouldFetchAffiliate =
      isAffiliateExpEnabled && !loadCachedAffiliateData() && isLoggedIn;
    // Check if paid early access cookie already exists
    const existingPaidEarlyAccessCookie = getCookie(PAID_EARLY_ACCESS_KEY);
    const shouldFetchPaidEarlyAccess =
      paidEarlyAccessEnabled &&
      isUserLoggedIn &&
      !existingPaidEarlyAccessCookie;

    // Check if we should fetch over network
    const shouldFetchSegment =
      isUserSegmentEnabled && isNewUser && fetchSegmentOverNetwork();

    // If cookie exists, hydrate Redux from it (without re-writing cookie)
    if (
      existingPaidEarlyAccessCookie &&
      typeof existingPaidEarlyAccessCookie === 'string' &&
      existingPaidEarlyAccessCookie !== PAID_EARLY_ACCESS_DEFAULT_VALUE
    ) {
      dispatch(setPaidEarlyAccessFromCookie(existingPaidEarlyAccessCookie));
    }

    // Return if no fetch needed
    if (
      !shouldFetchSegment &&
      !shouldFetchPaidEarlyAccess &&
      !shouldFetchAffiliate
    )
      return;

    fetchUserAttributes(attributes)
      .then((response) => {
        if (response) {
          // Handle paid early access data
          processPaidEarlyAccess(response);
          // Handle user segment data
          if (isUserSegmentEnabled && response.usersegment) {
            const segmentIds = response.usersegment?.segment || [];
            const isRepeated = segmentIds.some(
              (segmentId: string) => segmentId === repeatUser
            );
            const repeatUserValue = isRepeated
              ? REPEAT_USER_ONE
              : REPEAT_USER_ZERO;
            setCookie(IS_REPEAT_USER, repeatUserValue, REPEAT_USER_EXPIRY_DAYS);
          }
          if (isAffiliateExpEnabled) {
            storeAffiliateData(response.affiliate || {});
          }
        }
      })
      .catch((err) => {
        if (err) {
          logger.error(
            'Error fetching user attributes:',
            err,
            USER_SEGMENT_COOKIE_ERROR
          );
        }
      });
  }, [
    getAttributes,
    isAffiliateExpEnabled,
    loadCachedAffiliateData,
    isLoggedIn,
    paidEarlyAccessEnabled,
    isUserLoggedIn,
    isUserSegmentEnabled,
    fetchUserAttributes,
    dispatch,
    processPaidEarlyAccess,
    repeatUser,
    storeAffiliateData,
  ]);

  const handleCartRefreshLite = useCallback(async () => {
    try {
      await dispatch(cartLiteApi(apiParams, isConvenienceFeeEnabled));
    } catch (error) {
      logger.error('Error in handleCartRefreshLite:', error);
    }
  }, [dispatch, apiParams, isConvenienceFeeEnabled]);

  useEffect(() => {
    if (dataFetched && id) {
      handleFetchUserAttributes();
    }
  }, [dataFetched, handleFetchUserAttributes, id]);

  const isStepperEnabled = stepCounterExpEnabled || stepperEnabled;
  const cartRefreshLiteEnabled =
    isStepperEnabled &&
    cartApiConfigEnabled &&
    isPathEnabled(pathname, enabledPaths);
  useEffect(() => {
    if (dataFetched && cartRefreshLiteEnabled) {
      handleCartRefreshLite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFetched, cartRefreshLiteEnabled]);

  useEffect(() => {
    setUpPdpSessionCount(isPDPBannerShown);
  }, [isPDPBannerShown, productId]);

  useEffect(() => {
    window.requestIdleCallback(() => {
      fetchAuth();
    });
  }, [fetchAuth]);

  const isIocExperimentEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_IOC_CART_NUDGE, 'A')
  );

  const isAfDownloadCtaEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, DW_APP_BANNER, 'A')
  );

  const { iocValueExpirationTime, iocCookieExpiryTime } = useSelector(
    (state: State) => state.remoteConfig.configs?.cartNudgeConfig
  );

  /**
   * Fetches IOC (Install Offer Count) information if conditions are met.
   * If the current IOC value is not 3 and the IOC has expired, it fetches the
   * latest information from the API and updates the local storage and cookies
   * with the new value and expiration time.
   * @returns {void}
   */
  const fetchIocInfo = (): void => {
    const iocExpiryTime = localStorageService.getItem(IOC_EXPIRATION_TIME);
    const currentDate = new Date().getTime();
    const currentIocValue = getCookie(IOC_VALUE);
    const isIocExpired =
      !iocExpiryTime || currentDate > +iocExpiryTime || !currentIocValue;
    if ((currentIocValue != 3 && isIocExpired) || isAfDownloadCtaEnabled) {
      userInfoByAttribute(ATTRIBUTE_INSTALL_OFFER).then((response) => {
        const iocValue = response?.install_offer?.count;
        dispatch(setIocValue(iocValue));
        setCookie(IOC_VALUE, iocValue, iocCookieExpiryTime);
        const timeLimit =
          new Date().getTime() + iocValueExpirationTime * 3600000;
        localStorageService.setItem(IOC_EXPIRATION_TIME, timeLimit.toString());
      });
    }
  };

  // Calls user info api to fetch IOC value
  useEffect(() => {
    // checks if experiment is enabled and user is logged in
    if ((isIocExperimentEnabled || isAfDownloadCtaEnabled) && id) {
      fetchIocInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (dataFetched && formKey) {
      RequestConfig.setFormKey(formKey);
    }
  }, [dataFetched, formKey]);

  useEffect(() => {
    if (dataFetched && isDifferentialPricingEnabled && id) {
      const isLoggedIn = !!email;
      if (isLoggedIn) {
        setCookie(PRIVE_TIER_KEY, priveBand, 1);
      } else if (!isLoggedIn) {
        deleteCookie(PRIVE_TIER_KEY);
      }
    }
  }, [
    dataFetched,
    email,
    fetchPriveTier,
    id,
    isDifferentialPricingEnabled,
    priveBand,
  ]);

  useEffect(() => {
    const userData = {
      email,
      firstName,
      id,
      isPro,
      groupId,
      priveBand,
    };
    if (dataFetched) {
      trackUser(userData);
      if (showChatNow) {
        if (!isZendeskEnabled) {
          const isLoggedIn = !!email;
          if (isLoggedIn) {
            fetchUserHash();
          } else {
            loadVerloop({});
          }
        }
      }

      if (trackDeviceId) {
        handleDevice();
      }
    }
  }, [
    email,
    firstName,
    id,
    dataFetched,
    userName,
    fetchedOverNetwork,
    trackDeviceId,
    groupId,
    isPro,
    fetchUserHash,
    showChatNow,
    isZendeskEnabled,
    priveBand,
  ]);

  useEffect(() => {
    if (dataFetched) {
      const isLoggedIn = !!email;
      if (
        isLoggedIn &&
        getRegistration() &&
        window.location?.pathname?.indexOf(AUTH_URL) < 0
      ) {
        showProfile();
        deleteRegistration();
      }
    }
  }, [dataFetched, email, pathname, showProfile]);

  const handleAutoSignIn = useCallback(() => {
    pushData({ phfill: getMobileNumberLocal() ? 1 : 0 });
    const autoFillVar = getCookie(EXP_AB_AUTOFILL);

    if (isSmartLockActive) {
      if (!email) {
        addAutoSignInEvent();
      } else if (fetchedOverNetwork) {
        storeCredentials({ email, userName });
      }
    }
    if (
      autoFillVar === 'A' &&
      !isAutoFillShown() &&
      !email &&
      pageType === PageTypes.HOME
    ) {
      setAutoFillShown();
      setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
      showSignUp();
      return;
    }
    // else if ((autoFillVar === 'B') &&
    //   (!isAutoFillShown()) && (!email) &&
    //   (pageType !== PageTypes.PRODUCT) &&
    //   (pageType !== PageTypes.SEARCH_LISTING) &&
    //   (pageType !== PageTypes.LISTING)
    // ) {
    //     setAutoFillShown();
    //     showSignUp();
    //     return;
    // }
    else if (isAutoSignUp && !isAutoFillShown() && !email) {
      setAutoFillShown();
      setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
      showSignUp();
      return;
    }
  }, [
    isSmartLockActive,
    email,
    pageType,
    isAutoSignUp,
    fetchedOverNetwork,
    userName,
    showSignUp,
  ]);

  const suggestMobileNumber = useCallback(() => {
    suggestMobile()
      .then((res) => {
        const authMapSteps = getAuthSteps();
        deleteAuthSteps();
        if (res?.isEligible) {
          let action = trackingEvent.CTA_SUGGESTED_EMAIL;
          if (authMapSteps) {
            if (authMapSteps == LOGIN_CODES.EMAIL_PASSWORD) {
              action = trackingEvent.CTA_EMAIL_PASS_SOCIAL;
            }
            if (authMapSteps === LOGIN_CODES.GOOGLE_ONE_TAP) {
              action = trackingEvent.CTA_GOOGLE_ONE_TAP;
            }
            if (authMapSteps === LOGIN_CODES.EMAIL_OTP) {
              action = trackingEvent.CTA_EMAIL_OTP;
            } else if (authMapSteps > 1) {
              action = trackingEvent.CTA_SOCIAL;
            }
          }
          pushMobileVerificationEvents({ actionName: action, data: res });
          const encodedUrl = encodeURIComponent(
            `${window.location.pathname}${window.location.search}`
          );
          const url = `${MOBILE_VERIFICATION_URL}?redirect=${encodedUrl}`;
          const redirectUrl = checkValidHost(url, domains);
          setTimeout(() => {
            setShowIframe(true);
            setIframeUrl(redirectUrl);
          }, 500);
        }
      })
      .catch(() => {
        deleteAuthSteps();
      });
  }, [suggestMobile, domains]);

  useEffect(() => {
    if (dataFetched && configFlagsFetched) {
      handleAutoSignIn();
    }
  }, [configFlagsFetched, dataFetched, handleAutoSignIn]);

  useEffect(() => {
    if (!dataFetched || window.location?.pathname?.indexOf(AUTH_URL) === 0) {
      setShowIframe(false);
      return;
    }
    pushData({ cartCount: getCartCountFromStorage() });
    const authMapSteps = getAuthSteps();
    // 1 suggested email flow
    // 2 login source google
    // 3 facebook
    if (authMapSteps === LOGIN_CODES.GOOGLE) {
      pushData({ googleSuccess: true });
    }
    if (
      isSuggestedMobileMap &&
      dataFetched &&
      authMapSteps &&
      email &&
      !window.location.href.includes(GOOGLE_ONE_EXPERIEMNT) &&
      window.location?.pathname?.indexOf(MOBILE_VERIFICATION_URL) < 0
    ) {
      suggestMobileNumber();
    } else {
      deleteAuthSteps();
    }
  }, [dataFetched, email, suggestMobileNumber, isSuggestedMobileMap]);

  useEffect(() => {
    dispatch({ type: appEvents.SET_PATHNAME });
  });

  useEffect(() => {
    if (isPricesRevealedActive && bucketId) {
      pushData({ bucketId });
    }
  }, [bucketId, isPricesRevealedActive]);

  if (showIframe && dataFetched) {
    return (
      <FrameWrapper id="mobmapping_frame">
        <Iframe
          src={iframeUrl}
          allowFullScreen
        />
      </FrameWrapper>
    );
  }

  return (
    <>
      <WishList
        dataFetched={dataFetched}
        isLoggedIn={!!email}
      />
    </>
  );
};

export default Boot;
